/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { ToolbarInput } from "@fullcalendar/core";
import { Box, Grid, Stack } from "@mui/material";
import AutocompleteCheckboxes from "components/generals/AutocompleteCheckboxes";
import Button from "components/generals/Button";
import PermissionWrapper from "components/generals/PermissionWrapper";
import AppointmentCalendarView from "components/views/AppointmentCalendarView";
import AppointmentDateRangeModal from "components/views/AppointmentDateRangeModal";
import { CoreAPI_URLs } from "constants/http.const";
import { RoutesConstants } from "medicalsystem-shared";
import useHttp from "hooks/http.hook";
import { ProviderInterface, QueryResultInterface } from "medicalsystem-shared";
import moment, { unitOfTime } from "moment";
import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EntityPermissions, PermissionsUser } from "types";
import { ViewTypeEnum } from "types/view.enum";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";

/**
 * Props for the AppointmentCalendarContent component.
 * @interface AppointmentCalendarContentProps
 * @property {ViewTypeEnum} viewType - The type of view to display (day, week, month)
 */
interface AppointmentCalendarContentProps {
  viewType: ViewTypeEnum;
}

/**
 * Represents a color option for calendar items.
 * @interface OptionColor
 * @property {string} id - The unique identifier for the option
 * @property {string} color - The color code associated with the option
 */
export interface OptionColor {
  id: string;
  color: string;
}

/**
 * Content component for displaying an interactive appointment calendar.
 * Provides calendar views for scheduling and managing medical appointments with
 * provider filtering, color-coded events, and navigation controls.
 * @param {AppointmentCalendarContentProps} props - Component props
 * @returns {React.ReactElement} The appointment calendar content component
 */
const AppointmentCalendarContent: FC<AppointmentCalendarContentProps> = ({ viewType }) => {
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [openArrModalFromParent, setOpenArrModalFromParent] = useState(false);
  useEffect(() => {
    let rangeType: unitOfTime.StartOf;

    if (viewType === ViewTypeEnum.PAGE) {
      rangeType = "week";
    } else if (viewType === ViewTypeEnum.WIDGET) {
      rangeType = "day";
    }

    const startOfMonth = moment().startOf(rangeType).toDate();
    const endOfMonth = moment().endOf(rangeType).toDate();

    setStartDate(startOfMonth);
    setEndDate(endOfMonth);
  }, []);

  const toolbarHeader = useMemo<ToolbarInput | false>(() => {
    if (viewType === ViewTypeEnum.WIDGET) {
      return {
        left: "prev,next,today",
        right: "title",
      };
    } else if (viewType === ViewTypeEnum.PAGE) {
      return undefined;
    } else {
      return false;
    }
  }, [viewType]);

  const initialViewMode = useMemo<string>(
    () => (viewType === ViewTypeEnum.WIDGET ? "timeGridDay" : "timeGridWeek"),
    [viewType]
  );

  const addNewButton = useMemo<boolean>(() => viewType !== ViewTypeEnum.WIDGET, []);

  const [_isLoading, _error, sendRequest] = useHttp();
  const [doctorListResponse, setDoctorListResponse] = useState<ProviderInterface[]>([]);
  const [selectedDoctors, setSelectedDoctors] = useState<ProviderInterface[]>([]);
  const [optionColors, setOptionColors] = useState<OptionColor[]>([]);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);

  useEffect(() => {
    sendRequest(CoreAPI_URLs.DOCTOR_GET_ALL, onGetDoctorHandler, "GET");
  }, []);

  const onGetDoctorHandler = (doctorsResponse: QueryResultInterface<ProviderInterface>) => {
    setDoctorListResponse(doctorsResponse.results);
  };

  const handleSelectChange = (doctors: ProviderInterface[]) => {
    setSelectedDoctors(doctors);
  };

  return (
    <Box>
      {addNewButton && (
        <Grid
          container
          justifyContent="space-between"
          direction={{ xs: "column", sm: "row" }}
          mb={2}
        >
          <Stack alignItems="center" direction={{ xs: "column", sm: "row" }} gap={1} mb={1} id="cy-select-doctor">
            <AutocompleteCheckboxes
              options={doctorListResponse}
              label={t("appointmentCalendar.selectDoctors")}
              placeholder={t("appointmentCalendar.chooseDoctors")}
              onChange={handleSelectChange}
              optionColors={optionColors}
              setOptionColors={setOptionColors}
              getOptionLabel={(option) => option.person.name + " " + option.person.lastname}
            />
          </Stack>
          <Stack alignItems={{xs: "flex-start", sm: "center"}} direction={{ xs: "column", sm: "row" }} gap={1}>
            <PermissionWrapper
              requiredPermissions={[PermissionsUser[EntityPermissions.APPOINTMENTS].EDIT]}
            >
              <Box
                onClick={() => {
                  setOpenArrModalFromParent(true);
                }}
              >
                <Button color="dark">
                  {t("appointmentCalendar.viewPassAppointments")}
                </Button>
              </Box>
            </PermissionWrapper>
            <Button
              color="info"
              onClick={() => setIsDateRangeModalOpen(true)}
              startIcon={<SearchIcon />}
            >
              {t("appointmentCalendar.searchByDateRange")}
            </Button>
            <PermissionWrapper
              requiredPermissions={[PermissionsUser[EntityPermissions.APPOINTMENTS].CREATE]}
            >
              <Box
                onClick={() => {
                  navigate(RoutesConstants.NEW_APPOINTMENT);
                }}
              >
                <Button color="primary" id="cy-newAppointment">
                  {t("appointmentCalendar.newAppointment")}
                </Button>
              </Box>
            </PermissionWrapper>
          </Stack>
        </Grid>
      )}
      {startDate && endDate && (
        <AppointmentCalendarView
          startDate={startDate}
          endDate={endDate}
          {...(viewType !== ViewTypeEnum.PAGE && {
            headerToolbar: toolbarHeader,
            initialView: initialViewMode
          })}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          filterAppointmentsByDoctors={selectedDoctors}
          optionColors={optionColors}
          openArrModalFromParent={openArrModalFromParent}
          setOpenArrModalFromParent={setOpenArrModalFromParent}
        />
      )}
      
      {/* Date Range Search Modal */}
      <AppointmentDateRangeModal
        open={isDateRangeModalOpen}
        onClose={() => setIsDateRangeModalOpen(false)}
      />
    </Box>
  );
};

export default AppointmentCalendarContent;
